import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from '../../../redux/redux-store';
import { ProfileAvatar } from '../../avatar/profile-avatar';
import { useGetOrganizationClientsQuery, useGetClientDetailsQuery } from '../../../redux/organization/organization.endpoints';
import { CreateClientModal } from './create-client-modal';
import { Button } from 'primereact/button';
import { InviteUserModal } from './invite-user-modal';
import { EditBrandModal } from "./edit-brand-modal";
import { Skeleton } from "src/components/ui/skeleton";
import { AssignCampaignsModal } from "./assign-campaigns-modal";
import { CustomizeReporterModal } from "./customize-reporter-modal";
import { Search } from "lucide-react";
import { Input } from "src/components/ui/input";
import { Building, LogOut } from 'lucide-react';
import { signOut } from '../../../redux/user/user.async-thunks';
import { useNavigate } from 'react-router-dom';
import { ClientCard } from './ClientCard';
import { getUserStateActiveClient } from "src/redux/user/user.selectors";
import { DashboardAccessModal } from "./dashboard-access-modal";

type ModalType = 'invite' | 'branding' | 'campaigns' | 'reporter' | 'dashboard-access' | null;

const UserDetails = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [searchValue, setSearchValue] = useState("");
  const userState = useSelector((state: RootState) => state.userState);
  const activeClient = useSelector(getUserStateActiveClient);
  const isOrgAdmin = userState.permissions?.groups?.includes('org_admin');
  const isOrgUser = userState.permissions?.groups?.includes('org_user') || isOrgAdmin;
  const isClientAdmin = userState.permissions?.groups?.includes('client_admin');
  const isClientUser = userState.permissions?.groups?.includes('client_user') || isClientAdmin;
  const { data: clients, isLoading: isLoadingClients } = useGetOrganizationClientsQuery(undefined, {
    skip: !isOrgAdmin,
  });
  const { data: selectedClient, refetch: refetchSelectedClient } = useGetClientDetailsQuery(selectedClientId ?? '', {
    skip: !selectedClientId,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const filteredClients = clients?.filter(client =>
    client.title.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleModalClose = () => {
    setSelectedClientId(null);
    setModalType(null);
  };

  const handleContinueToCustomize = () => {
    refetchSelectedClient();
    setModalType('reporter');
  };

  useEffect(() => {
    if (selectedClientId) {
      refetchSelectedClient();
    }
  }, [selectedClientId]);

  return (
    <div className="relative max-w-full px-4 sm:px-6 lg:px-8">
      <div className="mb-8 sm:mb-12">
        <div className="flex flex-col sm:flex-row items-center justify-center rounded-2xl bg-white shadow-md p-4 sm:p-8 gap-4 sm:gap-6">
          <div className="bg-gray-100 rounded-full mx-auto">
            <ProfileAvatar largerSize />
          </div>
          <div className="flex flex-col flex-1 min-w-0 items-center sm:items-start text-center sm:text-left">
            {userState.fullname && userState.fullname !== 'None None' ? (
              <>
                <div className="text-lg font-bold truncate">
                  {userState.fullname}
                </div>
                <div className="text-base truncate">
                  {userState.email}
                </div>
              </>
            ) : (
              <div className="text-lg font-bold truncate">
                {userState.email}
              </div>
            )}
            <div className="flex items-center justify-center gap-1 mt-3 text-gray-700 text-base">
              <Building className="size-4 mb-1" />
              <span className="align-middle font-medium">
                Organization:
              </span>
              <span className="truncate align-middle">
                {userState.organization?.name || activeClient?.title || '—'}
              </span>
            </div>
            <button
              className="flex items-center justify-center gap-1 mt-3 text-sm font-medium text-black/50 hover:text-black transition"
              onClick={async() => {
                await dispatch(signOut() as any);
                navigate('/login');
              }}
              type="button"
            >
              <LogOut className="size-4" />
              Log out
            </button>
          </div>
        </div>
      </div>
      { (isOrgAdmin || isOrgUser) && (
        <div className="w-full">
          <div className="flex-column mb-4 gap-2">
            <div className="flex justify-between items-start mb-3">
              <h2 className="text-xl font-semibold tracking-tight">
                Clients
              </h2>
              {isOrgAdmin && (
                <Button
                  label="+"
                  onClick={() => setIsCreateModalOpen(true)}
                  className="p-button-secondary p-button-sm self-end bg-black/40 text-white text-xl rounded-full w-6 h-6"
                />
              )}
            </div>
            <div className="relative flex-1 w-full mb-4 shadow-sm rounded-xl bg-white">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search"
                value={searchValue}
                onChange={handleSearchChange}
                className="pl-10 bg-white rounded-xl placeholder:text-black/75 shadow-sm h-10 w-full"
              />
            </div>
            <div className="flex flex-col gap-4">
              {isLoadingClients ? (
                <div className="flex flex-col bg-white rounded-2xl shadow-md p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                    <div className="flex-shrink-0 flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white border border-gray-400 rounded-xl overflow-hidden">
                      <Skeleton className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl" />
                    </div>
                    <div className="flex-1 min-w-0 text-center sm:text-left">
                      <Skeleton className="h-6 w-3/4 mb-2" />
                      <div className="flex flex-col sm:flex-row gap-2 w-full mt-4">
                        <Skeleton className="h-10 flex-1" />
                        <Skeleton className="h-10 flex-1" />
                        <Skeleton className="h-10 flex-1" />
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <Skeleton className="h-[150px] w-full" />
                  </div>
                </div>
              ) : (
                filteredClients && filteredClients.length === 0 ? (
                  <div className="text-gray-500 pl-2">
                    No clients in this organization.
                  </div>
                ) : (
                  filteredClients?.slice().sort((a, b) => a.title.localeCompare(b.title)).map(client => (
                    <ClientCard
                      key={client.ext_client_id}
                      client={client}
                      canEdit={!!isOrgAdmin}
                      canInvite={!!isOrgAdmin}
                      canCustomize={!!isOrgAdmin}
                      canDashboardAccess={!!isOrgAdmin}
                      canPreview={!!isOrgUser || !!isOrgAdmin}
                      showActionsColumn={!!isOrgAdmin}
                      onEdit={() => {
                        setSelectedClientId(client.ext_client_id);
                        setModalType('branding');
                      }}
                      onInvite={() => {
                        setSelectedClientId(client.ext_client_id);
                        setModalType('invite');
                      }}
                      onCustomize={() => {
                        setSelectedClientId(client.ext_client_id);
                        setModalType('campaigns');
                      }}
                      onDashboardAccess={() => {
                        setSelectedClientId(client.ext_client_id);
                        setModalType('dashboard-access');
                      }}
                    />
                  ))
                )
              )}
            </div>
          </div>
        </div>
      )}
      {(isClientUser || isClientUser) && !(isOrgAdmin || isOrgUser) && activeClient?.ext_client_id && (
        <div className="w-full">
          <div className="flex-column mb-4 gap-2">
            <div className="flex flex-col gap-4">
              <ClientCard
                client={activeClient ?? null}
                canEdit={false}
                canInvite={isClientAdmin ?? false}
                canCustomize={false}
                canPreview={false}
                canDashboardAccess={isClientAdmin ?? false}
                showActionsColumn={isClientAdmin ?? false}
                onEdit={() => {}}
                onInvite={isClientAdmin ? () => {
                  setSelectedClientId(activeClient?.ext_client_id ?? null);
                  setModalType('invite');
                } : () => {}}
                onCustomize={() => {}}
                onDashboardAccess={() => {
                  setSelectedClientId(activeClient?.ext_client_id ?? null);
                  setModalType('dashboard-access');
                }}
              />
            </div>
          </div>
        </div>
      )}

      <CreateClientModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
      {selectedClientId && selectedClient && (
        <>
          <InviteUserModal
            isOpen={modalType === 'invite'}
            onClose={handleModalClose}
            clientId={selectedClientId}
          />
          <EditBrandModal
            isOpen={modalType === 'branding'}
            onClose={handleModalClose}
            client={selectedClient}
          />
          <AssignCampaignsModal
            isOpen={modalType === 'campaigns'}
            onClose={handleModalClose}
            client={selectedClient}
            onContinue={handleContinueToCustomize}
          />
          <CustomizeReporterModal
            isOpen={modalType === 'reporter'}
            onClose={handleModalClose}
            client={selectedClient}
          />
          <DashboardAccessModal
            isOpen={modalType === 'dashboard-access'}
            onClose={() => { handleModalClose(); refetchSelectedClient(); }}
            client={selectedClient}
          />
        </>
      )}
    </div>
  );
};

export default UserDetails;
